import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiClock, FiTarget, FiTrendingUp, FiFileText, FiFilter, FiX, FiCopy, FiPlay, FiActivity, FiUserPlus, FiSettings, FiCalendar, FiUpload, FiZap, FiChevronDown, FiChevronUp, FiMove } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableTaskItem from './SortableTaskItem';

interface CTOManager {
  id: string;
  email: string;
  name: string | null;
  title: string | null;
  granted_at: string;
}

interface Task {
  id: string;
  title: string;
  description: string | null;
  category: string;
  impact_level: number;
  impact_type: string;
  phase: string;
  estimated_hours: number;
  time_spent: number;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  assigned_to: string | null;
  assigned_to_email: string | null;
  assigned_to_name: string | null;
  project: string;
  priority_order: number;
}

const PROJECTS = [
  { id: 'snap_chart', label: 'Snap Chart', color: 'from-purple-600 to-cyan-500', icon: '📊' },
  { id: 'cab_bot_v2', label: 'Cab Bot V2', color: 'from-orange-500 to-yellow-400', icon: '🤖' }
];

const CATEGORIES = ['AI', 'Product', 'UX', 'Infra', 'Security', 'Investor', 'Growth', 'Bugfix', 'Legal'];
const IMPACT_TYPES = ['Valuation', 'Revenue', 'User', 'Institutional', 'Security', 'Stability'];
const PHASES = ['Phase 1', 'Phase 2', 'Phase 3', 'Phase 4'];
const STATUSES = ['Pending', 'In Progress', 'Completed'];

// Memoized color functions outside component
const getCategoryColor = (category: string): string => {
  const colors: Record<string, string> = {
    'AI': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    'Product': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    'UX': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
    'Infra': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    'Security': 'bg-red-500/20 text-red-300 border-red-500/30',
    'Investor': 'bg-green-500/20 text-green-300 border-green-500/30',
    'Growth': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    'Bugfix': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
    'Legal': 'bg-gray-500/20 text-gray-300 border-gray-500/30'
  };
  return colors[category] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
};

const getImpactColor = (level: number): string => {
  if (level >= 5) return 'text-red-400 bg-red-500/20';
  if (level >= 4) return 'text-orange-400 bg-orange-500/20';
  if (level >= 3) return 'text-yellow-400 bg-yellow-500/20';
  if (level >= 2) return 'text-blue-400 bg-blue-500/20';
  return 'text-green-400 bg-green-500/20';
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'Completed': return 'bg-green-500/20 text-green-300';
    case 'In Progress': return 'bg-cyan-500/20 text-cyan-300';
    default: return 'bg-gray-500/20 text-gray-300';
  }
};

// Format time helper - moved outside
const formatTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m > 0 ? `${h}h${m}m` : `${h}h`;
};

export default function AdminTasksPanel() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState<{
    date: string;
    weekNumber: number;
    summary: { tasksCompleted: number; highImpactCount: number; totalTime: string; impactScore: number; institutionalReadiness: number };
    impact: { institutional: Task[]; valuation: Task[]; security: Task[]; revenue: Task[] };
    highImpactTasks: Task[];
    categoryBreakdown: { name: string; count: number }[];
    phaseBreakdown: { name: string; count: number }[];
  } | null>(null);
  
  // Project filter state
  const [selectedProject, setSelectedProject] = useState<string>('snap_chart');
  
  // Bulk import state
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkPreview, setBulkPreview] = useState<{title: string; status: string}[]>([]);
  const [importingBulk, setImportingBulk] = useState(false);
  
  // CTO Manager access management
  const [ctoManagers, setCtoManagers] = useState<CTOManager[]>([]);
  const [showAccessPanel, setShowAccessPanel] = useState(false);
  const [newManagerEmail, setNewManagerEmail] = useState('');
  const [newManagerName, setNewManagerName] = useState('');
  const [newManagerTitle, setNewManagerTitle] = useState('');
  const [addingManager, setAddingManager] = useState(false);
  const [adminProfile, setAdminProfile] = useState<{ name: string; email: string } | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  
  // Collapsible sections
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(true);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Product',
    impact_level: 3,
    impact_type: 'User',
    phase: 'Phase 1',
    estimated_hours: 0,
    time_spent: 0,
    status: 'Pending',
    assigned_to: 'admin-self',
    assigned_to_email: '',
    assigned_to_name: '',
    project: 'snap_chart'
  });

  const [filters, setFilters] = useState({
    status: '',
    category: '',
    phase: '',
    impact_level: '',
    impact_type: ''
  });

  // Load data on mount
  useEffect(() => {
    fetchTasks();
    fetchCTOManagers();
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('user_profiles')
      .select('name, first_name, last_name, email')
      .eq('user_id', user.id)
      .single();
    
    if (data) {
      const displayName = data.name || 
        [data.first_name, data.last_name].filter(Boolean).join(' ') || 
        data.email?.split('@')[0] || 
        'Admin';
      setAdminProfile({ name: displayName, email: data.email || user.email || '' });
    }
  };

  const fetchCTOManagers = async () => {
    const { data, error } = await supabase
      .from('cto_managers')
      .select('*')
      .order('granted_at', { ascending: true });
    
    if (!error && data) {
      setCtoManagers(data as CTOManager[]);
    }
  };

  const addCTOManager = async () => {
    if (!newManagerEmail.trim()) return;
    setAddingManager(true);
    
    const { error } = await supabase
      .from('cto_managers')
      .insert([{
        email: newManagerEmail.trim().toLowerCase(),
        name: newManagerName.trim() || null,
        title: newManagerTitle.trim() || 'Team Member',
        granted_by: user?.id
      }]);
    
    if (!error) {
      setNewManagerEmail('');
      setNewManagerName('');
      setNewManagerTitle('');
      fetchCTOManagers();
    }
    setAddingManager(false);
  };

  const removeCTOManager = async (id: string) => {
    const { error } = await supabase
      .from('cto_managers')
      .delete()
      .eq('id', id);
    
    if (!error) {
      fetchCTOManagers();
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('admin_tasks')
      .select('*')
      .order('priority_order', { ascending: true });

    if (!error && data) {
      setTasks(data as Task[]);
    }
    setLoading(false);
  };

  // DnD Kit sensors - optimized for precision
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    setActiveTaskId(event.active.id as string);
  };

  // Handle drag end - reorder tasks with batch update
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveTaskId(null);
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    const projectTasks = tasks.filter(t => t.project === selectedProject && t.status !== 'Completed');
    const oldIndex = projectTasks.findIndex(t => t.id === active.id);
    const newIndex = projectTasks.findIndex(t => t.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedProjectTasks = arrayMove(projectTasks, oldIndex, newIndex);
    
    // Update local state immediately for responsiveness
    const updatedTasks = tasks.map(task => {
      if (task.project !== selectedProject || task.status === 'Completed') return task;
      const newOrder = reorderedProjectTasks.findIndex(t => t.id === task.id);
      return { ...task, priority_order: newOrder + 1 };
    });
    setTasks(updatedTasks);

    // Batch update in database - use Promise.all for performance
    const updates = reorderedProjectTasks.map((task, index) => 
      supabase
        .from('admin_tasks')
        .update({ priority_order: index + 1 })
        .eq('id', task.id)
    );
    
    await Promise.all(updates);
  }, [tasks, selectedProject]);

  // Get active task for drag overlay - memoized
  const activeTask = useMemo(() => 
    activeTaskId ? tasks.find(t => t.id === activeTaskId) : null
  , [activeTaskId, tasks]);

  // Quick finish task with optimistic update
  const finishTask = useCallback(async (task: Task) => {
    // Optimistic update
    setTasks(prev => prev.map(t => 
      t.id === task.id 
        ? { ...t, status: 'Completed', completed_at: new Date().toISOString() }
        : t
    ));

    const { error } = await supabase
      .from('admin_tasks')
      .update({ 
        status: 'Completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', task.id);

    if (error) {
      // Revert on error
      fetchTasks();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Resolve admin-self to actual values
    const resolvedAssignedTo = formData.assigned_to === 'admin-self' ? 'admin-self' : formData.assigned_to;
    const resolvedEmail = formData.assigned_to === 'admin-self' 
      ? (adminProfile?.email || user?.email || '') 
      : formData.assigned_to_email;
    const resolvedName = formData.assigned_to === 'admin-self' 
      ? (adminProfile?.name || 'Admin') 
      : formData.assigned_to_name;

    const taskData = {
      title: formData.title,
      description: formData.description || null,
      category: formData.category,
      impact_level: formData.impact_level,
      impact_type: formData.impact_type,
      phase: formData.phase,
      estimated_hours: formData.estimated_hours,
      time_spent: formData.time_spent,
      status: formData.status,
      created_by: user.id,
      completed_at: formData.status === 'Completed' ? new Date().toISOString() : null,
      assigned_to: resolvedAssignedTo || null,
      assigned_to_email: resolvedEmail || null,
      assigned_to_name: resolvedName || null,
      project: selectedProject
    };

    console.log('📝 Saving task with data:', taskData);

    if (editingTask) {
      const { error } = await supabase
        .from('admin_tasks')
        .update({
          ...taskData,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingTask.id);

      if (error) {
        console.error('❌ Error updating task:', error);
      } else {
        console.log('✅ Task updated successfully');
        fetchTasks();
        resetForm();
      }
    } else {
      const { error } = await supabase
        .from('admin_tasks')
        .insert([taskData]);

      if (error) {
        console.error('❌ Error creating task:', error);
      } else {
        console.log('✅ Task created successfully');
        fetchTasks();
        resetForm();
      }
    }
  };

  const toggleTaskStatus = async (task: Task) => {
    const newStatus = task.status === 'Pending' ? 'In Progress' : 
                      task.status === 'In Progress' ? 'Completed' : 'Pending';
    
    const { error } = await supabase
      .from('admin_tasks')
      .update({ 
        status: newStatus,
        completed_at: newStatus === 'Completed' ? new Date().toISOString() : null,
        updated_at: new Date().toISOString()
      })
      .eq('id', task.id);

    if (!error) fetchTasks();
  };

  const deleteTask = async (id: string) => {
    if (!confirm('Delete this task?')) return;
    const { error } = await supabase
      .from('admin_tasks')
      .delete()
      .eq('id', id);

    if (!error) fetchTasks();
  };

  const editTask = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      category: task.category,
      impact_level: task.impact_level,
      impact_type: task.impact_type,
      phase: task.phase,
      estimated_hours: task.estimated_hours || 0,
      time_spent: task.time_spent || 0,
      status: task.status,
      assigned_to: task.assigned_to || '',
      assigned_to_email: task.assigned_to_email || '',
      assigned_to_name: task.assigned_to_name || '',
      project: task.project || 'snap_chart'
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      category: 'Product',
      impact_level: 3,
      impact_type: 'User',
      phase: 'Phase 1',
      estimated_hours: 0,
      time_spent: 0,
      status: 'Pending',
      assigned_to: 'admin-self',
      assigned_to_email: adminProfile?.email || user?.email || '',
      assigned_to_name: adminProfile?.name || 'Admin',
      project: selectedProject
    });
  };

  // Memoized filtered tasks
  const filteredTasks = useMemo(() => tasks.filter(task => {
    if (task.project !== selectedProject) return false;
    if (filters.status && task.status !== filters.status) return false;
    if (filters.category && task.category !== filters.category) return false;
    if (filters.phase && task.phase !== filters.phase) return false;
    if (filters.impact_level && task.impact_level !== parseInt(filters.impact_level)) return false;
    if (filters.impact_type && task.impact_type !== filters.impact_type) return false;
    return true;
  }), [tasks, selectedProject, filters]);

  // Memoized active and completed task lists
  const activeTasks = useMemo(() => 
    filteredTasks
      .filter(t => t.status !== 'Completed')
      .sort((a, b) => a.priority_order - b.priority_order)
  , [filteredTasks]);

  const completedTasksList = useMemo(() => 
    filteredTasks
      .filter(t => t.status === 'Completed')
      .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime())
  , [filteredTasks]);

  // Analytics
  const totalTasks = tasks.length;
  const pendingTasks = tasks.filter(t => t.status === 'Pending').length;
  const inProgressTasks = tasks.filter(t => t.status === 'In Progress').length;
  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const totalEstimatedHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
  const totalMinutesSpent = tasks.reduce((sum, t) => sum + (t.time_spent || 0), 0);
  const totalHoursSpent = Math.floor(totalMinutesSpent / 60);
  const totalRemainingMinutes = totalMinutesSpent % 60;
  
  // Get start of current week (Monday)
  const getStartOfWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday
    const monday = new Date(now.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  };

  // Get start of current month
  const getStartOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const startOfWeek = getStartOfWeek();
  const startOfMonth = getStartOfMonth();

  // Filter tasks worked on this week (created or updated this week with time spent)
  const thisWeekWorkedTasks = tasks.filter(t => {
    const createdAt = new Date(t.created_at);
    const updatedAt = new Date(t.updated_at);
    return (createdAt >= startOfWeek || updatedAt >= startOfWeek) && (t.time_spent || 0) > 0;
  });

  // Filter completed tasks this week (for count)
  const thisWeekCompletedTasks = tasks.filter(t => 
    t.status === 'Completed' && t.completed_at && new Date(t.completed_at) >= startOfWeek
  );

  // Filter tasks worked on this month
  const thisMonthWorkedTasks = tasks.filter(t => {
    const createdAt = new Date(t.created_at);
    const updatedAt = new Date(t.updated_at);
    return (createdAt >= startOfMonth || updatedAt >= startOfMonth) && (t.time_spent || 0) > 0;
  });

  // Filter completed tasks this month (for count)
  const thisMonthCompletedTasks = tasks.filter(t => 
    t.status === 'Completed' && t.completed_at && new Date(t.completed_at) >= startOfMonth
  );

  // Group time by person for this week (all worked tasks)
  const weeklyTimeByPerson = thisWeekWorkedTasks.reduce((acc, task) => {
    const person = task.assigned_to_name || 'Unassigned';
    acc[person] = (acc[person] || 0) + (task.time_spent || 0);
    return acc;
  }, {} as Record<string, number>);

  // Group time by person for this month (all worked tasks)
  const monthlyTimeByPerson = thisMonthWorkedTasks.reduce((acc, task) => {
    const person = task.assigned_to_name || 'Unassigned';
    acc[person] = (acc[person] || 0) + (task.time_spent || 0);
    return acc;
  }, {} as Record<string, number>);

  // Group tasks count by person for this week (completed only)
  const weeklyTasksByPerson = thisWeekCompletedTasks.reduce((acc, task) => {
    const person = task.assigned_to_name || 'Unassigned';
    acc[person] = (acc[person] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Total weekly minutes (all worked tasks)
  const totalWeeklyMinutes = thisWeekWorkedTasks.reduce((sum, t) => sum + (t.time_spent || 0), 0);
  const weeklyHours = Math.floor(totalWeeklyMinutes / 60);
  const weeklyMins = totalWeeklyMinutes % 60;

  // Total monthly minutes (all worked tasks)
  const totalMonthlyMinutes = thisMonthWorkedTasks.reduce((sum, t) => sum + (t.time_spent || 0), 0);
  const monthlyHours = Math.floor(totalMonthlyMinutes / 60);
  const monthlyMins = totalMonthlyMinutes % 60;

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  const weeklyCompletedTasks = tasks.filter(t => 
    t.status === 'Completed' && t.completed_at && new Date(t.completed_at) >= oneWeekAgo
  );
  const weeklyImpactScore = weeklyCompletedTasks.reduce((sum, t) => sum + t.impact_level, 0);
  
  const institutionalTasks = tasks.filter(t => t.impact_type === 'Institutional');
  const completedInstitutional = institutionalTasks.filter(t => t.status === 'Completed').length;
  const institutionalReadiness = institutionalTasks.length > 0 
    ? Math.round((completedInstitutional / institutionalTasks.length) * 100) 
    : 0;

  // Format time helper
  const formatTime = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    return m > 0 ? `${h}h${m}m` : `${h}h`;
  };

  // Generate Weekly Report
  // Structured report data for professional UI
  const getReportData = () => {
    const highImpactTasks = weeklyCompletedTasks.filter(t => t.impact_level >= 4);
    const totalMinutesThisWeek = weeklyCompletedTasks.reduce((sum, t) => sum + (t.time_spent || 0), 0);
    const hoursThisWeek = Math.floor(totalMinutesThisWeek / 60);
    const minutesThisWeek = totalMinutesThisWeek % 60;
    const formattedTime = minutesThisWeek > 0 ? `${hoursThisWeek}h ${minutesThisWeek}m` : `${hoursThisWeek}h`;
    const institutionalTasks = weeklyCompletedTasks.filter(t => t.impact_type === 'Institutional');
    const valuationTasks = weeklyCompletedTasks.filter(t => t.impact_type === 'Valuation');
    const securityTasks = weeklyCompletedTasks.filter(t => t.impact_type === 'Security');
    const revenueTasks = weeklyCompletedTasks.filter(t => t.impact_type === 'Revenue');
    
    const categoryBreakdown = CATEGORIES.map(cat => ({
      name: cat,
      count: weeklyCompletedTasks.filter(t => t.category === cat).length
    })).filter(c => c.count > 0);

    const phaseBreakdown = PHASES.map(phase => ({
      name: phase,
      count: weeklyCompletedTasks.filter(t => t.phase === phase).length
    })).filter(p => p.count > 0);

    return {
      date: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      weekNumber: Math.ceil((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 604800000),
      summary: {
        tasksCompleted: weeklyCompletedTasks.length,
        highImpactCount: highImpactTasks.length,
        totalTime: formattedTime,
        impactScore: weeklyImpactScore,
        institutionalReadiness: institutionalReadiness
      },
      impact: {
        institutional: institutionalTasks,
        valuation: valuationTasks,
        security: securityTasks,
        revenue: revenueTasks
      },
      highImpactTasks,
      categoryBreakdown,
      phaseBreakdown
    };
  };

  const generateReport = () => {
    setReportData(getReportData());
    setShowReport(true);
  };

  const copyReportAsText = () => {
    if (!reportData) return;
    const text = `
WEEKLY CTO REPORT — Week ${reportData.weekNumber}
${reportData.date}

EXECUTIVE SUMMARY
• Tasks Completed: ${reportData.summary.tasksCompleted}
• High-Impact Tasks: ${reportData.summary.highImpactCount}
• Time Invested: ${reportData.summary.totalTime}
• Impact Score: ${reportData.summary.impactScore}
• Institutional Readiness: ${reportData.summary.institutionalReadiness}%

STRATEGIC IMPACT
${reportData.impact.valuation.length > 0 ? `Valuation: ${reportData.impact.valuation.map(t => t.title).join(', ')}` : 'Valuation: —'}
${reportData.impact.institutional.length > 0 ? `Institutional: ${reportData.impact.institutional.map(t => t.title).join(', ')}` : 'Institutional: —'}
${reportData.impact.revenue.length > 0 ? `Revenue: ${reportData.impact.revenue.map(t => t.title).join(', ')}` : 'Revenue: —'}
${reportData.impact.security.length > 0 ? `Security: ${reportData.impact.security.map(t => t.title).join(', ')}` : 'Security: —'}

HIGH-IMPACT COMPLETIONS
${reportData.highImpactTasks.length > 0 ? reportData.highImpactTasks.map(t => `• ${t.title} (${t.category}, Impact ${t.impact_level}/5)`).join('\n') : '• No high-impact tasks this week'}

BREAKDOWN BY CATEGORY
${reportData.categoryBreakdown.map(c => `• ${c.name}: ${c.count}`).join('\n') || '—'}

BREAKDOWN BY PHASE
${reportData.phaseBreakdown.map(p => `• ${p.name}: ${p.count}`).join('\n') || '—'}

Generated by Snap Chart AI — CTO Dashboard
    `.trim();
    navigator.clipboard.writeText(text);
    toast.success('Report copied to clipboard');
  };

  // Parse bulk text into tasks
  const parseBulkText = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    const parsed: {title: string; status: string}[] = [];
    
    for (const line of lines) {
      // Match patterns like "1. Task name : Done" or "2. Task name" or "- Task name: Done"
      const match = line.match(/^[\d\-\.\)\s]*(.+?)(?:\s*[:\-]\s*(Done|Completed|In Progress|Pending))?$/i);
      if (match) {
        const title = match[1].trim();
        const statusRaw = match[2]?.toLowerCase() || '';
        let status = 'Pending';
        
        if (statusRaw.includes('done') || statusRaw.includes('completed')) {
          status = 'Completed';
        } else if (statusRaw.includes('progress')) {
          status = 'In Progress';
        }
        
        if (title.length > 0) {
          parsed.push({ title, status });
        }
      }
    }
    
    return parsed;
  };

  // Handle bulk text change
  const handleBulkTextChange = (text: string) => {
    setBulkText(text);
    setBulkPreview(parseBulkText(text));
  };

  // Import bulk tasks
  const handleBulkImport = async () => {
    if (!user || bulkPreview.length === 0) return;
    setImportingBulk(true);

    const tasksToInsert = bulkPreview.map(task => ({
      title: task.title,
      description: null,
      category: 'Product',
      impact_level: 3,
      impact_type: 'User',
      phase: 'Phase 1',
      estimated_hours: 0,
      time_spent: 0,
      status: task.status,
      created_by: user.id,
      completed_at: task.status === 'Completed' ? new Date().toISOString() : null,
      assigned_to: 'admin-self',
      assigned_to_email: adminProfile?.email || user.email || '',
      assigned_to_name: adminProfile?.name || 'Admin',
      project: selectedProject
    }));

    const { error } = await supabase
      .from('admin_tasks')
      .insert(tasksToInsert);

    if (!error) {
      console.log(`✅ Imported ${tasksToInsert.length} tasks`);
      fetchTasks();
      setShowBulkImport(false);
      setBulkText('');
      setBulkPreview([]);
    } else {
      console.error('❌ Error importing tasks:', error);
    }
    
    setImportingBulk(false);
  };

  const copyReport = () => {
    navigator.clipboard.writeText(reportText);
  };

  // Group tasks by phase
  const tasksByPhase = PHASES.reduce((acc, phase) => {
    acc[phase] = filteredTasks.filter(t => t.phase === phase);
    return acc;
  }, {} as Record<string, Task[]>);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Project Tabs - Ultra Minimal Silicon Valley 2026 */}
      <div className="flex items-center gap-2">
        {PROJECTS.map(project => {
          const projectTaskCount = tasks.filter(t => t.project === project.id).length;
          const projectActiveCount = tasks.filter(t => t.project === project.id && t.status !== 'Completed').length;
          const isSelected = selectedProject === project.id;
          
          return (
            <button
              key={project.id}
              onClick={() => setSelectedProject(project.id)}
              className={`relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                isSelected
                  ? 'bg-white text-gray-900'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              <span className="flex items-center gap-2">
                {project.label}
                <span className={`text-xs font-mono ${isSelected ? 'text-gray-500' : 'text-gray-600'}`}>
                  {projectActiveCount}/{projectTaskCount}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Header - Text-First Minimal */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">
            {PROJECTS.find(p => p.id === selectedProject)?.label || 'Tasks'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">Institutional-grade task orchestration</p>
        </div>
        
        {/* Team Badge - Minimal */}
        <button 
          onClick={() => setShowAccessPanel(!showAccessPanel)}
          className="flex items-center gap-3 px-4 py-2 rounded-full bg-gray-800/40 hover:bg-gray-800/60 border border-gray-700/30 transition-all"
        >
          <div className="flex -space-x-1.5">
            {['J', 'W'].slice(0, ctoManagers.length + 1).map((initial, i) => (
              <div 
                key={i} 
                className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-600 to-gray-700 flex items-center justify-center text-white text-xs font-medium ring-2 ring-gray-900"
              >
                {ctoManagers[i]?.name?.[0]?.toUpperCase() || initial}
              </div>
            ))}
          </div>
          <span className="text-sm text-gray-400">{ctoManagers.length + 1}</span>
        </button>
      </div>

      {/* Access Management Panel */}
      <AnimatePresence>
        {showAccessPanel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-900/80 border border-cyan-500/30 rounded-xl p-4 overflow-hidden"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FiUserPlus className="w-5 h-5 text-cyan-400" />
              Manage CTO Manager Access
            </h3>
            
            {/* Add new manager */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <input
                type="email"
                placeholder="Email address"
                value={newManagerEmail}
                onChange={(e) => setNewManagerEmail(e.target.value)}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Name"
                value={newManagerName}
                onChange={(e) => setNewManagerName(e.target.value)}
                className="sm:w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Title (CEO, CTO...)"
                value={newManagerTitle}
                onChange={(e) => setNewManagerTitle(e.target.value)}
                className="sm:w-32 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-cyan-500 focus:outline-none"
              />
              <button
                onClick={addCTOManager}
                disabled={addingManager || !newManagerEmail.trim()}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-cyan-500 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              >
                <FiPlus className="w-4 h-4" />
                Add
              </button>
            </div>
            
            {/* Current managers list */}
            <div className="space-y-2">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Current CTO Managers</div>
              {ctoManagers.length === 0 ? (
                <div className="text-sm text-gray-400 py-2">No CTO managers added yet</div>
              ) : (
                ctoManagers.map((manager) => (
                  <div key={manager.id} className="flex items-center justify-between bg-gray-800/50 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-sm text-white">{manager.name || 'Unnamed'}</span>
                      <span className="text-xs text-cyan-400 ml-2">({manager.title || 'Team Member'})</span>
                      <span className="text-xs text-gray-400 ml-2">{manager.email}</span>
                    </div>
                    <button
                      onClick={() => removeCTOManager(manager.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      title="Remove access"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Analytics Dashboard - Ultra Minimal */}
      <div className="border-t border-gray-800/50 pt-6">
        <button 
          onClick={() => setShowAnalytics(!showAnalytics)}
          className="w-full flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-medium text-white">Analytics</h2>
            <span className="flex items-center gap-1.5 text-xs text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              Live
            </span>
          </div>
          <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showAnalytics ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence>
          {showAnalytics && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              className="px-6 pb-6 space-y-6"
            >
              {/* Stats Row - Minimal Text-Based */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-6 py-6">
                <div>
                  <div className="text-3xl font-semibold text-white font-mono">{totalTasks}</div>
                  <div className="text-xs text-gray-500 mt-1">Total</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-yellow-400 font-mono">{pendingTasks}</div>
                  <div className="text-xs text-gray-500 mt-1">Pending</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-cyan-400 font-mono">{inProgressTasks}</div>
                  <div className="text-xs text-gray-500 mt-1">Active</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-green-400 font-mono">{completedTasks}</div>
                  <div className="text-xs text-gray-500 mt-1">Done</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-white font-mono">{formatTime(totalMinutesSpent)}</div>
                  <div className="text-xs text-gray-500 mt-1">Time logged</div>
                </div>
                <div>
                  <div className="text-3xl font-semibold text-white font-mono">{weeklyImpactScore}</div>
                  <div className="text-xs text-gray-500 mt-1">Impact score</div>
                </div>
              </div>

              {/* Time Tracking - Minimal Tables */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4 border-t border-gray-800/50">
                {/* This Week */}
                <div>
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-400">This Week</h3>
                    <span className="text-xs text-gray-600">
                      {startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – Today
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-2xl font-semibold text-white font-mono">{formatTime(totalWeeklyMinutes)}</div>
                    <div className="text-xs text-gray-500 mt-1">{thisWeekWorkedTasks.length} tasks · {thisWeekCompletedTasks.length} completed</div>
                  </div>

                  <div className="space-y-2">
                    {Object.entries(weeklyTimeByPerson).length > 0 ? (
                      Object.entries(weeklyTimeByPerson)
                        .sort((a, b) => b[1] - a[1])
                        .map(([person, minutes]) => (
                          <div key={person} className="flex items-center justify-between py-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300">
                                {person.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm text-gray-300">{person}</span>
                              <span className="text-xs text-gray-600">({weeklyTasksByPerson[person] || 0})</span>
                            </div>
                            <span className="text-sm text-white font-mono">{formatTime(minutes)}</span>
                          </div>
                        ))
                    ) : (
                      <div className="text-gray-600 text-sm py-2">No time logged this week</div>
                    )}
                  </div>
                </div>

                {/* This Month */}
                <div>
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-400">This Month</h3>
                    <span className="text-xs text-gray-600">
                      {startOfMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  
                  <div className="mb-4">
                    <div className="text-2xl font-semibold text-white font-mono">{formatTime(totalMonthlyMinutes)}</div>
                    <div className="text-xs text-gray-500 mt-1">{thisMonthWorkedTasks.length} tasks · {thisMonthCompletedTasks.length} completed</div>
                  </div>

                  <div className="space-y-2">
                    {Object.entries(monthlyTimeByPerson).length > 0 ? (
                      Object.entries(monthlyTimeByPerson)
                        .sort((a, b) => b[1] - a[1])
                        .map(([person, minutes]) => {
                          const monthlyCompletedCount = thisMonthCompletedTasks.filter(t => (t.assigned_to_name || 'Unassigned') === person).length;
                          return (
                            <div key={person} className="flex items-center justify-between py-2">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-xs text-gray-300">
                                  {person.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-300">{person}</span>
                                <span className="text-xs text-gray-600">({monthlyCompletedCount})</span>
                              </div>
                              <span className="text-sm text-white font-mono">{formatTime(minutes)}</span>
                            </div>
                          );
                        })
                    ) : (
                      <div className="text-gray-600 text-sm py-2">No time logged this month</div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Actions - Minimal Uniform Style */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 rounded-lg transition-colors"
        >
          + Add Task
        </button>
        <button
          onClick={() => setShowBulkImport(true)}
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
        >
          Bulk Import
        </button>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            showFilters ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Filters
        </button>
        <button
          onClick={generateReport}
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
        >
          Report
        </button>
      </div>

      {/* Filters Panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-900/60 border border-gray-700/50 rounded-xl p-4"
          >
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">All</option>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">All</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Phase</label>
                <select
                  value={filters.phase}
                  onChange={(e) => setFilters({ ...filters, phase: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">All</option>
                  {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Impact Level</label>
                <select
                  value={filters.impact_level}
                  onChange={(e) => setFilters({ ...filters, impact_level: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">All</option>
                  {[1,2,3,4,5].map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Impact Type</label>
                <select
                  value={filters.impact_type}
                  onChange={(e) => setFilters({ ...filters, impact_type: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">All</option>
                  {IMPACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            
            <button
              onClick={() => setFilters({ status: '', category: '', phase: '', impact_level: '', impact_type: '' })}
              className="mt-3 text-sm text-gray-400 hover:text-white"
            >
              Clear all filters
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Weekly Report Modal */}
      <AnimatePresence>
        {showReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReport(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#0a0a0a] border border-gray-800 rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-8 py-6 border-b border-gray-800 flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-1">Weekly Report</p>
                  <h2 className="text-2xl font-semibold text-white tracking-tight">
                    CTO Performance Summary
                  </h2>
                  {reportData && (
                    <p className="text-sm text-gray-500 mt-1">
                      Week {reportData.weekNumber} · {reportData.date}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={copyReportAsText}
                    className="px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 rounded-lg transition-colors"
                  >
                    Copy Report
                  </button>
                  <button
                    onClick={() => setShowReport(false)}
                    className="p-2 text-gray-500 hover:text-white transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-8 py-6 overflow-y-auto max-h-[calc(85vh-120px)] space-y-8">
                {reportData && (
                  <>
                    {/* Executive KPIs */}
                    <div className="grid grid-cols-5 gap-4">
                      <div className="text-center py-4 px-3 bg-gray-900/50 rounded-xl border border-gray-800">
                        <p className="text-3xl font-bold text-white font-mono">{reportData.summary.tasksCompleted}</p>
                        <p className="text-xs text-gray-500 mt-1">Tasks</p>
                      </div>
                      <div className="text-center py-4 px-3 bg-gray-900/50 rounded-xl border border-gray-800">
                        <p className="text-3xl font-bold text-white font-mono">{reportData.summary.highImpactCount}</p>
                        <p className="text-xs text-gray-500 mt-1">High-Impact</p>
                      </div>
                      <div className="text-center py-4 px-3 bg-gray-900/50 rounded-xl border border-gray-800">
                        <p className="text-3xl font-bold text-white font-mono">{reportData.summary.totalTime}</p>
                        <p className="text-xs text-gray-500 mt-1">Time</p>
                      </div>
                      <div className="text-center py-4 px-3 bg-gray-900/50 rounded-xl border border-gray-800">
                        <p className="text-3xl font-bold text-white font-mono">{reportData.summary.impactScore}</p>
                        <p className="text-xs text-gray-500 mt-1">Impact</p>
                      </div>
                      <div className="text-center py-4 px-3 bg-gray-900/50 rounded-xl border border-gray-800">
                        <p className="text-3xl font-bold text-white font-mono">{reportData.summary.institutionalReadiness}%</p>
                        <p className="text-xs text-gray-500 mt-1">Readiness</p>
                      </div>
                    </div>

                    {/* Strategic Impact */}
                    <div>
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">Strategic Impact</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-300">Valuation</span>
                            <span className="text-sm font-mono text-white">{reportData.impact.valuation.length}</span>
                          </div>
                          {reportData.impact.valuation.length > 0 ? (
                            <div className="space-y-1">
                              {reportData.impact.valuation.slice(0, 3).map((t, i) => (
                                <p key={i} className="text-xs text-gray-500 truncate">{t.title}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-600">No valuation tasks</p>
                          )}
                        </div>
                        <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-300">Institutional</span>
                            <span className="text-sm font-mono text-white">{reportData.impact.institutional.length}</span>
                          </div>
                          {reportData.impact.institutional.length > 0 ? (
                            <div className="space-y-1">
                              {reportData.impact.institutional.slice(0, 3).map((t, i) => (
                                <p key={i} className="text-xs text-gray-500 truncate">{t.title}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-600">No institutional tasks</p>
                          )}
                        </div>
                        <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-300">Revenue</span>
                            <span className="text-sm font-mono text-white">{reportData.impact.revenue.length}</span>
                          </div>
                          {reportData.impact.revenue.length > 0 ? (
                            <div className="space-y-1">
                              {reportData.impact.revenue.slice(0, 3).map((t, i) => (
                                <p key={i} className="text-xs text-gray-500 truncate">{t.title}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-600">No revenue tasks</p>
                          )}
                        </div>
                        <div className="p-4 bg-gray-900/30 rounded-xl border border-gray-800/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-300">Security</span>
                            <span className="text-sm font-mono text-white">{reportData.impact.security.length}</span>
                          </div>
                          {reportData.impact.security.length > 0 ? (
                            <div className="space-y-1">
                              {reportData.impact.security.slice(0, 3).map((t, i) => (
                                <p key={i} className="text-xs text-gray-500 truncate">{t.title}</p>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-600">No security tasks</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* High-Impact Completions */}
                    {reportData.highImpactTasks.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">High-Impact Completions</h3>
                        <div className="space-y-2">
                          {reportData.highImpactTasks.map((task, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-gray-900/30 rounded-lg border border-gray-800/50">
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-mono text-gray-500 bg-gray-800 px-2 py-0.5 rounded">{task.category}</span>
                                <span className="text-sm text-white">{task.title}</span>
                              </div>
                              <span className="text-xs font-mono text-gray-400">Impact {task.impact_level}/5</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Breakdown */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">By Category</h3>
                        <div className="space-y-2">
                          {reportData.categoryBreakdown.map((cat, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-gray-400">{cat.name}</span>
                              <span className="text-white font-mono">{cat.count}</span>
                            </div>
                          ))}
                          {reportData.categoryBreakdown.length === 0 && (
                            <p className="text-sm text-gray-600">No data</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">By Phase</h3>
                        <div className="space-y-2">
                          {reportData.phaseBreakdown.map((phase, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="text-gray-400">{phase.name}</span>
                              <span className="text-white font-mono">{phase.count}</span>
                            </div>
                          ))}
                          {reportData.phaseBreakdown.length === 0 && (
                            <p className="text-sm text-gray-600">No data</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="pt-4 border-t border-gray-800">
                      <p className="text-xs text-gray-600 text-center">
                        Generated by Snap Chart AI · CTO Dashboard
                      </p>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Import Modal */}
      <AnimatePresence>
        {showBulkImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBulkImport(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-900 border border-gray-700 rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FiUpload className="w-5 h-5 text-orange-400" />
                  Bulk Import Tasks
                </h3>
                <button
                  onClick={() => setShowBulkImport(false)}
                  className="p-1.5 text-gray-400 hover:text-white"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-gray-400 text-sm mb-2">
                  Paste your tasks from Notepad. Supported formats:
                </p>
                <div className="bg-gray-800/50 rounded-lg p-3 text-xs text-gray-500 font-mono">
                  <div>1. Task name : Done</div>
                  <div>2. Another task</div>
                  <div>- Task with dash : In Progress</div>
                  <div>Task without number</div>
                </div>
              </div>

              <textarea
                value={bulkText}
                onChange={(e) => handleBulkTextChange(e.target.value)}
                placeholder="Paste your tasks here..."
                className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-orange-500 focus:outline-none resize-none"
              />

              {bulkPreview.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300 font-medium">Preview ({bulkPreview.length} tasks)</span>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {bulkPreview.map((task, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-white">{task.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          task.status === 'Completed' ? 'bg-green-500/20 text-green-300' :
                          task.status === 'In Progress' ? 'bg-cyan-500/20 text-cyan-300' :
                          'bg-gray-500/20 text-gray-300'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowBulkImport(false)}
                  className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={bulkPreview.length === 0 || importingBulk}
                  className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {importingBulk ? (
                    <>
                      <FiClock className="w-4 h-4 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FiUpload className="w-4 h-4" />
                      Import {bulkPreview.length} Tasks
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Task Form Modal - Silicon Valley 2026 */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={resetForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="relative bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl shadow-purple-500/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-purple-500/5 via-transparent to-cyan-500/5 pointer-events-none" />
              
              {/* Header */}
              <div className="relative flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-white tracking-tight">
                    {editingTask ? 'Edit Task' : 'New Task'}
                  </h3>
                  <p className="text-gray-500 text-sm mt-1">Fill in the details below</p>
                </div>
                <motion.button 
                  onClick={resetForm} 
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 text-gray-500 hover:text-white bg-gray-800/50 hover:bg-gray-700/50 rounded-xl transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </motion.button>
              </div>
              
              <form onSubmit={handleSubmit} className="relative space-y-6">
                {/* Title */}
                <div className="space-y-2">
                  <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                    Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                    placeholder="Enter task title..."
                    required
                  />
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 min-h-[100px] focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all resize-none"
                    placeholder="Add a description..."
                  />
                </div>
                
                {/* Category, Phase, Status - 3 columns */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Category
                    </label>
                    <div className="relative">
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                      >
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Phase
                    </label>
                    <div className="relative">
                      <select
                        value={formData.phase}
                        onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                        className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                      >
                        {PHASES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Status
                    </label>
                    <div className="relative">
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                </div>
                
                {/* Impact & Time - 4 columns */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Impact (1-5)
                    </label>
                    <div className="relative">
                      <select
                        value={formData.impact_level}
                        onChange={(e) => setFormData({ ...formData, impact_level: parseInt(e.target.value) })}
                        className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                      >
                        {[1,2,3,4,5].map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Impact Type
                    </label>
                    <div className="relative">
                      <select
                        value={formData.impact_type}
                        onChange={(e) => setFormData({ ...formData, impact_type: e.target.value })}
                        className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                      >
                        {IMPACT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Est. Hours
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.estimated_hours}
                      onChange={(e) => setFormData({ ...formData, estimated_hours: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="0"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                      Time (min)
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.time_spent}
                      onChange={(e) => setFormData({ ...formData, time_spent: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Assignee */}
                <div className="space-y-2">
                  <label className="block text-gray-400 text-xs font-medium uppercase tracking-wider">
                    Assign To
                  </label>
                  <div className="relative">
                    <select
                      value={formData.assigned_to || 'admin-self'}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        if (selectedValue === 'admin-self') {
                          setFormData({
                            ...formData,
                            assigned_to: 'admin-self',
                            assigned_to_email: adminProfile?.email || user?.email || '',
                            assigned_to_name: adminProfile?.name || 'Admin'
                          });
                        } else {
                          const manager = ctoManagers.find(m => m.id === selectedValue);
                          if (manager) {
                            setFormData({
                              ...formData,
                              assigned_to: manager.id,
                              assigned_to_email: manager.email,
                              assigned_to_name: manager.name || manager.email
                            });
                          }
                        }
                      }}
                      className="w-full appearance-none bg-gray-800/60 border border-gray-700/50 rounded-xl px-4 py-3 text-white focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all cursor-pointer"
                    >
                      {adminProfile && (
                        <option value="admin-self">{adminProfile.name}</option>
                      )}
                      {ctoManagers.map(manager => (
                        <option key={manager.id} value={manager.id}>
                          {manager.name || manager.email}
                        </option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-3 pt-6 border-t border-gray-800">
                  <button
                    type="submit"
                    className="flex-1 py-3.5 rounded-xl font-semibold text-white bg-white/15 hover:bg-white/20 border border-white/10 transition-all"
                  >
                    {editingTask ? 'Update Task' : 'Create Task'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-8 py-3.5 rounded-xl font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tasks with Drag & Drop */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-gray-900/40 border border-gray-700/30 rounded-2xl p-5 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gray-800 rounded-xl" />
                <div className="flex-1 space-y-3">
                  <div className="flex gap-2">
                    <div className="w-16 h-5 bg-gray-800 rounded-lg" />
                    <div className="w-20 h-5 bg-gray-800 rounded-lg" />
                  </div>
                  <div className="w-3/4 h-5 bg-gray-800 rounded-lg" />
                  <div className="w-1/2 h-4 bg-gray-800 rounded-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredTasks.length === 0 ? (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16 bg-gray-900/30 rounded-2xl border border-gray-700/30"
        >
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-600/20 to-cyan-500/20 rounded-2xl flex items-center justify-center">
            <FiPlus className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-400 mb-2">No tasks yet</p>
          <p className="text-gray-600 text-sm">Create your first task to get started</p>
        </motion.div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="space-y-6">
            {/* Active Tasks - Priority Queue */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-white flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-cyan-500 animate-pulse shadow-lg shadow-purple-500/30" />
                  Priority Queue
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-500/15 text-purple-300 border border-purple-500/30">
                    {activeTasks.length} active
                  </span>
                </h3>
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <FiMove className="w-3 h-3" />
                  Drag to prioritize
                </span>
              </div>
              
              <SortableContext
                items={activeTasks.map(t => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {activeTasks.map((task, index) => (
                    <SortableTaskItem
                      key={task.id}
                      task={task}
                      index={index}
                      onEdit={editTask}
                      onDelete={deleteTask}
                      onToggleStatus={toggleTaskStatus}
                      onFinish={finishTask}
                      getCategoryColor={getCategoryColor}
                      getStatusColor={getStatusColor}
                      getImpactColor={getImpactColor}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>

            {/* Drag Overlay */}
            <DragOverlay>
              {activeTask ? (
                <motion.div 
                  className="bg-gray-900 border-2 border-purple-500 rounded-2xl p-4 shadow-2xl shadow-purple-500/40"
                  initial={{ rotate: 0, scale: 1 }}
                  animate={{ rotate: 2, scale: 1.03 }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border ${getCategoryColor(activeTask.category)}`}>
                      {activeTask.category}
                    </span>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${getStatusColor(activeTask.status)}`}>
                      {activeTask.status}
                    </span>
                  </div>
                  <h4 className="font-semibold text-white">{activeTask.title}</h4>
                </motion.div>
              ) : null}
            </DragOverlay>

            {/* Completed Tasks - Collapsible */}
            {completedTasksList.length > 0 && (
              <motion.div 
                className="bg-gray-900/30 border border-gray-700/30 rounded-2xl overflow-hidden"
                initial={false}
              >
                <button 
                  onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-800/30 transition-colors"
                >
                  <h3 className="text-base font-semibold text-gray-400 flex items-center gap-3">
                    <FiCheck className="w-5 h-5 text-green-400" />
                    Completed
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-500/15 text-green-400 border border-green-500/30">
                      {completedTasksList.length}
                    </span>
                  </h3>
                  <motion.div
                    animate={{ rotate: showCompletedTasks ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiChevronDown className="w-5 h-5 text-gray-500" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {showCompletedTasks && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pb-4 space-y-2"
                    >
                      {completedTasksList.map(task => (
                        <div
                          key={task.id}
                          className="bg-gray-800/30 border border-gray-700/20 rounded-xl p-4 opacity-80 hover:opacity-100 transition-opacity"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <span className={`px-2 py-0.5 rounded text-xs font-medium border opacity-60 ${getCategoryColor(task.category)}`}>
                                  {task.category}
                                </span>
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-400">
                                  ✓ Done
                                </span>
                              </div>
                              <h4 className="font-medium text-gray-500 line-through">{task.title}</h4>
                              <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                                {task.time_spent > 0 && <span>{formatTime(task.time_spent)}</span>}
                                {task.completed_at && (
                                  <span className="text-green-500/70">
                                    {new Date(task.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => toggleTaskStatus(task)}
                                className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors"
                                title="Reopen"
                              >
                                <FiCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => editTask(task)}
                                className="p-2 rounded-lg bg-gray-800/50 text-gray-500 hover:text-white transition-colors"
                                title="Edit"
                              >
                                <FiEdit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteTask(task.id)}
                                className="p-2 rounded-lg bg-gray-800/50 text-gray-500 hover:text-red-400 transition-colors"
                                title="Delete"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </DndContext>
      )}
    </div>
  );
}
